import { useCallback, useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  TrendingUp, TrendingDown, Info, Sparkles, Loader2, Home, LineChart as LineIcon,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";

const BASE = process.env.REACT_APP_BACKEND_URL;

const eur = (v) => (v == null ? "—" : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v));
const semLabel = (ver) => {
  if (!ver) return "—";
  const m = /^(\d{4})H(\d)$/.exec(ver);
  return m ? `${m[2] === "1" ? "1°" : "2°"} semestre ${m[1]}` : ver;
};
const confColor = (lvl) => ({
  "Molto alta": "bg-emerald-500", "Buona": "bg-[#007BFF]", "Media": "bg-amber-500", "Limitata": "bg-rose-500",
}[lvl] || "bg-slate-400");

const STATES = [
  { v: "da_ristrutturare", l: "Da ristrutturare" }, { v: "da_aggiornare", l: "Da aggiornare" },
  { v: "buono", l: "Buono" }, { v: "ristrutturato", l: "Ristrutturato" }, { v: "nuovo", l: "Nuovo/eccellente" },
];
const EXTERNAL = [{ v: "nessuno", l: "Nessuno" }, { v: "balcone", l: "Balcone" }, { v: "terrazzo", l: "Terrazzo" }, { v: "giardino", l: "Giardino" }];
const PARKING = [{ v: "nessuno", l: "Nessuno" }, { v: "posto_auto", l: "Posto auto" }, { v: "garage", l: "Garage" }];
const BRIGHT = [{ v: "bassa", l: "Bassa" }, { v: "normale", l: "Normale" }, { v: "alta", l: "Alta" }];

const Sel = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</label>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full mt-1 bg-secondary border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-[#007BFF]">
      <option value="">—</option>
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </div>
);

export const PropertyValuation = ({ token, buildingId, idImmobile, index = 0 }) => {
  const [val, setVal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [qOpen, setQOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ans, setAns] = useState({ state: "", has_elevator: "", floor: "", external: "", parking: "", brightness: "", surface_m2: "" });
  const auth = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!buildingId || !idImmobile) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/api/public/condomino/valuation?building_id=${encodeURIComponent(buildingId)}&id_immobile=${encodeURIComponent(idImmobile)}`, { headers: auth });
      setVal(await r.json());
      const h = await fetch(`${BASE}/api/public/condomino/valuation/history?building_id=${encodeURIComponent(buildingId)}&id_immobile=${encodeURIComponent(idImmobile)}`, { headers: auth });
      const hd = await h.json();
      setHistory(hd.history || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [buildingId, idImmobile]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const saveQuestionnaire = async () => {
    setSaving(true);
    try {
      const body = { building_id: buildingId, id_immobile: idImmobile, ...ans,
        has_elevator: ans.has_elevator === "" ? null : ans.has_elevator === "si",
        surface_m2: ans.surface_m2 ? parseFloat(String(ans.surface_m2).replace(",", ".")) : null };
      const r = await fetch(`${BASE}/api/public/condomino/valuation/qualitative-data`, {
        method: "POST", headers: { "Content-Type": "application/json", ...auth }, body: JSON.stringify(body) });
      const d = await r.json();
      if (r.ok) { setVal(d); setQOpen(false); load(); }
    } catch { /* noop */ }
    finally { setSaving(false); }
  };

  if (loading) return <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Calcolo della valutazione…</div>;
  if (!val) return null;

  if (val.status !== "READY") {
    return (
      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 px-3 py-2.5 text-[12px] text-amber-800 dark:text-amber-200" data-testid={`valuation-unavailable-${index}`}>
        {val.message || "Valutazione non disponibile al momento."}
      </div>
    );
  }

  const trendUp = (val.zone_trend_12m || 0) >= 0;
  const chartData = history.map((h) => ({ label: semLabel(h.dataset_version), value: h.estimated_value }));

  return (
    <div className="mt-4 rounded-2xl border border-[#007BFF]/25 bg-gradient-to-br from-[#007BFF]/[0.06] to-transparent p-4" data-testid={`valuation-card-${index}`}>
      <h3 className="font-semibold text-sm flex items-center gap-2 mb-3 text-foreground">
        <Home className="h-4 w-4 text-[#007BFF]" />Valore del tuo immobile
      </h3>

      <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Valore stimato</div>
          <div className="font-head text-3xl font-extrabold tracking-tight text-foreground" data-testid={`valuation-value-${index}`}>{eur(val.estimated_value)}</div>
          <div className="text-[12px] text-muted-foreground" data-testid={`valuation-range-${index}`}>{eur(val.estimated_low)} – {eur(val.estimated_high)}</div>
        </div>
        <div className="sm:ml-auto grid grid-cols-2 gap-x-5 gap-y-1.5">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Valore al m²</div>
            <div className="text-sm font-semibold">{val.estimated_price_m2 ? `€${new Intl.NumberFormat("it-IT").format(val.estimated_price_m2)}/m²` : "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Andamento zona 12 mesi</div>
            <div className={`text-sm font-semibold flex items-center gap-1 ${val.zone_trend_12m == null ? "text-muted-foreground" : trendUp ? "text-emerald-600" : "text-rose-600"}`} data-testid={`valuation-trend-${index}`}>
              {val.zone_trend_12m == null ? "—" : (<>{trendUp ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}{trendUp ? "+" : ""}{val.zone_trend_12m}%</>)}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between text-[11px] mb-1">
          <span className="text-muted-foreground">Affidabilità della stima</span>
          <span className="font-semibold" data-testid={`valuation-confidence-${index}`}>{val.confidence_score}/100 — {val.confidence_level}</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div className={`h-full ${confColor(val.confidence_level)} transition-all`} style={{ width: `${val.confidence_score}%` }} />
        </div>
      </div>

      <div className="mt-2 text-[11px] text-muted-foreground">Ultimo aggiornamento: {semLabel(val.dataset_version)}</div>

      {val.zone_precise === false && (
        <div className="mt-1 text-[10px] text-amber-700 dark:text-amber-400" data-testid={`valuation-zone-approx-${index}`}>
          Stima basata sui valori medi del comune (zona OMI puntuale non ancora disponibile).
        </div>
      )}
      {val.surface_source && val.surface_source.startsWith("stimata") && (
        <div className="mt-1 text-[10px] text-amber-700 dark:text-amber-400" data-testid={`valuation-surface-approx-${index}`}>
          {val.surface_source === "stimata_vani"
            ? `Superficie stimata dai vani catastali (${val.surface_used} m²)`
            : val.surface_source === "stimata_rendita"
              ? `Superficie stimata dalla rendita catastale (${val.surface_used} m²)`
              : val.surface_source === "stimata_media"
                ? `Superficie stimata sulla media di immobili simili (${val.surface_used} m²)`
                : `Superficie stimata sui valori tipici della categoria (${val.surface_used} m²)`}
          : inserisci i m² reali con "Migliora la stima" per un valore più preciso.
        </div>
      )}

      {val.zone_avg_price_m2 != null && val.vs_zone_pct != null && (
        <div className="mt-3 rounded-xl bg-white/60 dark:bg-white/5 border border-border p-3" data-testid={`valuation-zone-compare-${index}`}>
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-muted-foreground">Il tuo €/m² rispetto alla media di zona</span>
            <span className={`font-semibold ${val.vs_zone_pct >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid={`valuation-vs-zone-${index}`}>
              {val.vs_zone_pct >= 0 ? "+" : ""}{val.vs_zone_pct}%
            </span>
          </div>
          <div className="relative h-2 rounded-full bg-muted overflow-hidden">
            {/* media zona al centro (50%) */}
            <div className="absolute top-0 bottom-0 left-1/2 w-px bg-muted-foreground/50" />
            <div className={`absolute top-0 bottom-0 ${val.vs_zone_pct >= 0 ? "left-1/2 bg-emerald-500" : "right-1/2 bg-rose-500"}`}
              style={{ width: `${Math.min(50, Math.abs(val.vs_zone_pct) * 2)}%` }} />
          </div>
          <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-1">
            <span>Media zona: €{new Intl.NumberFormat("it-IT").format(val.zone_avg_price_m2)}/m²</span>
            {val.liquidity_level && val.liquidity_level !== "n.d." && (
              <span data-testid={`valuation-liquidity-${index}`}>Mercato: liquidità {val.liquidity_level}</span>
            )}
          </div>
        </div>
      )}

      {chartData.length >= 2 && (
        <div className="mt-3" data-testid={`valuation-history-${index}`}>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1"><LineIcon className="h-3 w-3" />Andamento valutazione</div>
          <div className="h-24">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 8, left: 8, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis hide domain={["dataMin", "dataMax"]} />
                <Tooltip formatter={(v) => eur(v)} />
                <Line type="monotone" dataKey="value" stroke="#007BFF" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Dialog open={qOpen} onOpenChange={setQOpen}>
          <DialogTrigger asChild>
            <Button data-testid={`valuation-improve-btn-${index}`} className="h-8 bg-[#007BFF] hover:bg-[#0069D9] text-white text-xs rounded-full px-4">
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />Migliora la stima
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid={`valuation-questionnaire-${index}`}>
            <DialogHeader><DialogTitle>Migliora la precisione della stima</DialogTitle></DialogHeader>
            <p className="text-[12px] text-muted-foreground -mt-2">Facoltativo. Poche domande per affinare la valutazione.</p>
            <div className="grid grid-cols-2 gap-3 mt-1">
              {(!val.surface_used || !["cadastral", "visura", "user"].includes(val.surface_source)) && (
                <div className="col-span-2">
                  <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Superficie (m²){(val.surface_source === "consistenza" || (val.surface_source && val.surface_source.startsWith("stimata"))) ? " — migliora molto la stima" : ""}</label>
                  <input data-testid={`q-surface-${index}`} value={ans.surface_m2} onChange={(e) => setAns((a) => ({ ...a, surface_m2: e.target.value }))}
                    className="w-full mt-1 bg-secondary border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-[#007BFF]" placeholder="es. 82" inputMode="decimal" />
                </div>
              )}
              <div className="col-span-2"><Sel label="Stato immobile" testid={`q-state-${index}`} value={ans.state} onChange={(v) => setAns((a) => ({ ...a, state: v }))} options={STATES} /></div>
              <Sel label="Ascensore" testid={`q-elevator-${index}`} value={ans.has_elevator} onChange={(v) => setAns((a) => ({ ...a, has_elevator: v }))} options={[{ v: "si", l: "Sì" }, { v: "no", l: "No" }]} />
              <div>
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Piano</label>
                <input data-testid={`q-floor-${index}`} value={ans.floor} onChange={(e) => setAns((a) => ({ ...a, floor: e.target.value }))}
                  className="w-full mt-1 bg-secondary border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-[#007BFF]" placeholder="es. 3" />
              </div>
              <Sel label="Spazi esterni" testid={`q-external-${index}`} value={ans.external} onChange={(v) => setAns((a) => ({ ...a, external: v }))} options={EXTERNAL} />
              <Sel label="Parcheggio" testid={`q-parking-${index}`} value={ans.parking} onChange={(v) => setAns((a) => ({ ...a, parking: v }))} options={PARKING} />
              <div className="col-span-2"><Sel label="Luminosità" testid={`q-brightness-${index}`} value={ans.brightness} onChange={(v) => setAns((a) => ({ ...a, brightness: v }))} options={BRIGHT} /></div>
            </div>
            <DialogFooter>
              <Button data-testid={`q-save-${index}`} onClick={saveQuestionnaire} disabled={saving} className="bg-[#007BFF] hover:bg-[#0069D9] text-white">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Aggiorna stima
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid={`valuation-how-btn-${index}`} className="h-8 text-xs rounded-full px-4">
              <Info className="h-3.5 w-3.5 mr-1.5" />Come viene calcolata?
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid={`valuation-how-${index}`}>
            <DialogHeader><DialogTitle>Come viene calcolata la stima</DialogTitle></DialogHeader>
            <div className="text-[13px] text-muted-foreground leading-relaxed space-y-3">
              <p>Sedio elabora una stima indicativa di mercato utilizzando i dati catastali disponibili dell'unità immobiliare e le quotazioni dell'Osservatorio del Mercato Immobiliare (OMI) dell'Agenzia delle Entrate relative alla zona.</p>
              <p>Il modello considera, quando disponibili, superficie, categoria e classe catastale, rendita relativa agli immobili comparabili presenti nel sistema, fascia di valore OMI e altri indicatori statistici.</p>
              <p>La stima non costituisce una perizia professionale e il valore effettivamente ottenibile sul mercato può dipendere da caratteristiche non rilevabili dai dati catastali.</p>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <p className="text-[10px] text-muted-foreground/70 mt-3 leading-relaxed" data-testid={`valuation-disclaimer-${index}`}>
        La valutazione ha carattere esclusivamente informativo e indicativo e non costituisce una perizia immobiliare,
        una certificazione del valore né una garanzia circa il prezzo ottenibile in caso di vendita. Il valore effettivo
        può essere influenzato da caratteristiche, stato manutentivo e condizioni di mercato non interamente rilevabili dai dati utilizzati.
      </p>
    </div>
  );
};

export default PropertyValuation;
